import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBalanceScale, FaSearch, FaPhone, FaMapMarkerAlt, FaPlus, FaBuilding
} from 'react-icons/fa';
import './PretrialServices.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PretrialServices = () => {
  const [services, setServices] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    counties: [],
    recordTypes: []
  });
  const [filters, setFilters] = useState({
    search: '',
    county: '',
    recordType: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFilterOptions();
    fetchServices();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchServices();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/pretrial-services/filters/options`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setFilterOptions(data.data);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Build query string
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.county) params.append('county', filters.county);
      if (filters.recordType) params.append('recordType', filters.recordType);

      const response = await fetch(`${API_URL}/pretrial-services?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setServices(data.data);
        setError('');
      } else {
        setError('Failed to load pretrial services');
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load pretrial services');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      county: '',
      recordType: ''
    });
  };

  const handleServiceClick = (serviceId) => {
    navigate(`/address-book/pretrial-services/${serviceId}`);
  };

  const getRecordTypeBadge = (recordType) => {
    if (!recordType) return null;

    let color = '#7f8c8d';
    let label = recordType;

    if (recordType.includes('Government_County')) {
      color = '#27ae60';
      label = 'County Program';
    } else if (recordType.includes('Private_Provider')) {
      color = '#e67e22';
      label = 'Private Provider';
    } else if (recordType.includes('Private_Contract')) {
      color = '#d35400';
      label = 'Contract Service';
    } else if (recordType.includes('Municipal')) {
      color = '#3498db';
      label = 'Municipal';
    } else if (recordType.includes('Federal')) {
      color = '#000000';
      label = 'Federal';
    } else if (recordType.includes('No_Services')) {
      color = '#95a5a6';
      label = 'No Services';
    } else if (recordType.includes('State')) {
      color = '#2c3e50';
      label = 'State Coordination';
    }

    return { color, label };
  };

  // Filter out "No_Services" records by default unless specifically searching
  const displayServices = services.filter(service => {
    if (filters.recordType === 'No_Services') return true;
    if (filters.search) return true;
    return service.record_type !== 'No_Services';
  });

  if (loading && services.length === 0) {
    return (
      <div className="pretrial-services-container">
        <div className="loading">Loading pretrial services...</div>
      </div>
    );
  }

  return (
    <div className="pretrial-services-container">
      {/* Header */}
      <div className="pretrial-services-header">
        <div className="header-left">
          <FaBalanceScale size={32} />
          <div>
            <h1>Montana Pretrial Services</h1>
            <p className="subtitle">{displayServices.length} pretrial service programs found</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="pretrial-services-filters">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by organization, county, or city..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>County</label>
            <select
              value={filters.county}
              onChange={(e) => handleFilterChange('county', e.target.value)}
              className="filter-select"
            >
              <option value="">All Counties</option>
              {filterOptions.counties.map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Service Type</label>
            <select
              value={filters.recordType}
              onChange={(e) => handleFilterChange('recordType', e.target.value)}
              className="filter-select"
            >
              <option value="">All Types</option>
              {filterOptions.recordTypes.map(type => (
                <option key={type} value={type}>{getRecordTypeBadge(type)?.label || type}</option>
              ))}
            </select>
          </div>

          {(filters.search || filters.county || filters.recordType) && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Services List */}
      <div className="services-list">
        {displayServices.length === 0 ? (
          <div className="no-results">
            <FaBalanceScale size={48} />
            <p>No pretrial services found matching your criteria</p>
          </div>
        ) : (
          displayServices.map(service => {
            const badge = getRecordTypeBadge(service.record_type);
            return (
              <div
                key={service.id}
                className="service-card"
                onClick={() => handleServiceClick(service.id)}
              >
                <div className="service-header">
                  <div className="service-title">
                    <FaBalanceScale className="service-icon" />
                    <h3>{service.organization_name || service.name}</h3>
                  </div>
                  {badge && (
                    <span className="type-badge" style={{ backgroundColor: badge.color }}>
                      {badge.label}
                    </span>
                  )}
                </div>

                <div className="service-info">
                  <div className="info-row">
                    {(service.county_jurisdiction || service.county) && (
                      <div className="info-item">
                        <FaBuilding className="info-icon" />
                        <span>{service.county_jurisdiction || service.county} County</span>
                      </div>
                    )}

                    {(service.address_city || service.city) && (
                      <div className="info-item">
                        <FaMapMarkerAlt className="info-icon" />
                        <span>{service.address_city || service.city}, MT</span>
                      </div>
                    )}

                    {service.phone && (
                      <div className="info-item">
                        <FaPhone className="info-icon" />
                        <span>{service.phone}</span>
                      </div>
                    )}
                  </div>

                  {service.address_street && (
                    <div className="address-detail">
                      {service.address_street}
                    </div>
                  )}

                  {service.type_classification && (
                    <div className="classification-tag">
                      {service.type_classification.replace(/_/g, ' ')}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PretrialServices;
